import React from "react";
import { Control } from "react-hook-form";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { RecipeSourceType } from "../../../../config/enums/RecipeSourceType.enum";

interface IngredientTableProps {
  ingredientList: any[];
  control: Control<any>;
  validateTotalValue: (id: string, value: string) => boolean;
  validateRecipeValuePercent: (value: any) => boolean | string;
  validationError: { id: string; error: string };
  getSupplierName: (supplierData: any, ingredientId: string, supplierId: string) => string;
  suppliers: any;
  produceTypes?: any[];
  countries?: any[];
  disabled?: boolean;
}

const isCultivationRow = (ingredient: any): boolean =>
  ingredient?.sourceType === RecipeSourceType.CULTIVATION;

// The "row id" is what ties a row's value input to its validation error, and
// to the saved recipe row. A cultivation row has no ingredientId, so it uses
// its Farm Gate id instead.
const getRowId = (ingredient: any): string | undefined =>
  isCultivationRow(ingredient)
    ? ingredient?.data?.reference?.farmGateId
    : ingredient?.data?.reference?.ingredientId;

const getFieldName = (ingredient: any): string =>
  isCultivationRow(ingredient)
    ? `cultivation-${ingredient?.data?.reference?.farmGateId}.value`
    : `ingredient-${ingredient?.data?.reference?.supplierId}-${ingredient?.data?.reference?.ingredientId}.value`;

const getRowKey = (ingredient: any): string =>
  isCultivationRow(ingredient)
    ? `cultivation-${ingredient?.data?.reference?.farmGateId}`
    : `ingredient-${ingredient?.data?.reference?.supplierId}-${ingredient?.data?.reference?.ingredientId}`;

const getIngredientName = (ingredient: any, produceTypes?: any[]): string => {
  if (!isCultivationRow(ingredient)) {
    return ingredient?.data?.label ?? "";
  }
  const produceType = ingredient?.data?.reference?.produceType ?? ingredient?.produceType;
  const match = produceTypes?.find((p: any) => parseInt(p.value) === parseInt(produceType));
  return match?.text ?? String(produceType ?? "");
};

const getCountryName = (ingredient: any, countries?: any[]): string => {
  if (!isCultivationRow(ingredient)) {
    return "–";
  }
  const originCountry = ingredient?.data?.reference?.originCountry ?? ingredient?.originCountry;
  if (!originCountry) {
    return "–";
  }
  const match = countries?.find((c: any) => parseInt(c.value) === parseInt(originCountry));
  return match?.text ?? String(originCountry);
};

/**
 * Separate component for rendering ingredient table
 * Follows Single Responsibility Principle - only renders ingredient table
 */
export const RecipeIngredientTable: React.FC<IngredientTableProps> = ({
  ingredientList,
  control,
  validateTotalValue,
  validateRecipeValuePercent,
  validationError,
  getSupplierName,
  suppliers,
  produceTypes,
  countries,
  disabled = false
}) => {
  if (!ingredientList || ingredientList.length === 0) {
    return null;
  }

  return (
    <div className="tw-space-y-3">
      <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">
        Ingredient (s)
      </h3>
      <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-gray-50">
            <tr>
              <th className="tw-px-3.5 tw-py-3.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-1/3">
                Ingredient
              </th>
              <th className="tw-px-3.5 tw-py-3.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-1/4">
                Supplier
              </th>
              <th className="tw-px-3.5 tw-py-3.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-1/6">
                Country
              </th>
              <th className="tw-px-3.5 tw-py-3.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-1/4">
                Value %
              </th>
            </tr>
          </thead>
          <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
            {ingredientList.map((ingredient: any) => {
              const rowId = getRowId(ingredient);
              return (
                <tr className="tw-border-b hover:tw-bg-gray-50" key={getRowKey(ingredient)}>
                  <td className="tw-px-3.5 tw-py-2.5">
                    <p className="tw-text-sm tw-text-gray-700">
                      {getIngredientName(ingredient, produceTypes)}
                    </p>
                  </td>
                  <td className="tw-px-3.5 tw-py-2.5">
                    <p className="tw-text-sm tw-text-gray-700">
                      {isCultivationRow(ingredient)
                        ? "Own cultivation"
                        : getSupplierName(
                            suppliers,
                            ingredient?.data?.reference.ingredientId,
                            ingredient?.data?.reference?.supplierId
                          )}
                    </p>
                  </td>
                  <td className="tw-px-3.5 tw-py-2.5">
                    <p className="tw-text-sm tw-text-gray-700">
                      {getCountryName(ingredient, countries)}
                    </p>
                  </td>
                  <td className="tw-px-3.5 tw-py-2.5">
                    <FormInputNumber
                      name={getFieldName(ingredient) as any}
                      onChangeFunction={(e: any) => {
                        validateTotalValue(rowId as string, e.target.value);
                      }}
                      placeholder="Enter value %"
                      control={control}
                      label=""
                      required={true}
                      errorMessage="This field is required"
                      enableMinus={false}
                      rules={{
                        validate: {
                          valueCheck: (v: any) => validateRecipeValuePercent(v)
                        }
                      }}
                      disabled={disabled}
                    />
                    {validationError.id === rowId && (
                      <span className="tw-text-red-600 tw-text-xs tw-mt-1 tw-block">
                        {validationError.error}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
